import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useEffect, useState, lazy, Suspense } from 'react';
import ScrollToTop from './Components/ScrollToTop';
import ScrollingText from './Components/Scrolling/ScrollingText';
import Navbar from './Components/Navbar/Navbar';
import Footer from './Components/Footer/Footer';
import LoadingSpinner from './Components/LoadingSpinner/LoadingSpinner'; // You'll need to create this

// Lazy load all pages for better performance
const Home = lazy(() => import('./Pages/Home/Home'));
const About = lazy(() => import('./Pages/About/About'));
const Collection = lazy(() => import('./Pages/Collection/Collection'));
const CartPage = lazy(() => import('./Pages/Cart/CartPage'));
const ProductPage = lazy(() => import('./Pages/Product/ProductPage'));
const CheckoutPage = lazy(() => import('./Pages/CheckoutPage/CheckoutPage'));
const ListProduct = lazy(() => import('./Pages/AdminPanel/ListProduct'));
const TermsAndConditions = lazy(() => import('./Pages/Terms/TermsandConditions'));
const RefundReturnPolicy = lazy(() => import('./Pages/Refund/RefundReturnPolicy'));
const ShippingPolicy = lazy(() => import('./Pages/Shipping/ShippingPolicy'));
const PrivacyPolicy = lazy(() => import('./Pages/Privacy/PrivacyPolicy'));
const CustomizePage = lazy(() => import('./Pages/CustomizePage/CustomizePage'));
const Blogs = lazy(() => import('./Pages/Blogs/Blogs'));
const ContactUs = lazy(() => import('./Pages/ContactUs/ContactUs'));
const PaymentSuccessPage = lazy(() => import('./Pages/PaymentSuccess/PaymentSuccessPage'));
const AdminPage = lazy(() => import('./Pages/AdminPanel/AdminPage'));
const TestimonialForm = lazy(() => import('./Pages/AdminPanel/TestimonialForm'));
const BannerPanel = lazy(() => import('./Pages/AdminPanel/BannerPanel'));
const BlogForm = lazy(() => import('./Pages/AdminPanel/BlogForm'));
const BlogList = lazy(() => import('./Pages/AdminPanel/BlogList'));
const BlogDetail = lazy(() => import('./Pages/Blogs/BlogDetail'));
const SubCollections = lazy(() => import('./Pages/SubCollections/SubCollections'));
const Login = lazy(() => import('./Components/Auth/Login'));
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const CategoryPanel = lazy(() => import('./Pages/AdminPanel/CategoryPanel'));
const CuratedCollectionForm = lazy(() => import('./Pages/AdminPanel/CuratedCollectionForm'));
const CuratedCollectionsList = lazy(() => import('./Pages/AdminPanel/CuratedCollectionList'));
const CreateCouponForm = lazy(() => import('./Pages/AdminPanel/CreateCouponform'));
const CouponList = lazy(() => import('./Pages/AdminPanel/CouponList'));
const ProtectedRoute = lazy(() => import('./Components/Auth/ProtectedRoute'));

function App() {
  const [isBackendAwake, setIsBackendAwake] = useState(false);
  const BACKEND_URL = 'https://sc-backend-9nhi.onrender.com';

  // Function to ping the backend
  const pingBackend = async () => {
    try {
      const startTime = Date.now();
      const response = await fetch(`${BACKEND_URL}/api/ping`);
      const endTime = Date.now();
      
      console.log(`Backend responded in ${endTime - startTime}ms`);
      setIsBackendAwake(true);
      return true;
    } catch (err) {
      console.log('Backend ping failed:', err);
      return false;
    }
  };

  useEffect(() => {
    // Initial ping when site loads - with retry logic
    const wakeBackend = async () => {
      setIsBackendAwake(false);
      let success = await pingBackend();
      
      // Retry up to 3 times if failed
      let retries = 0;
      while (!success && retries < 3) {
        console.log(`Retrying backend wake-up (${retries + 1}/3)...`);
        await new Promise(r => setTimeout(r, 2000)); // Wait 2 seconds before retry
        success = await pingBackend();
        retries++;
      }
    };
    
    wakeBackend();

    // Ping every 14 minutes to keep the server awake (Render free tier sleeps after 15 mins)
    const keepWarmInterval = setInterval(() => {
      pingBackend();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(keepWarmInterval);
  }, []);

  // Preload critical data that might be needed across the app
  useEffect(() => {
    if (isBackendAwake) {
      // Preload any essential data here
      // For example, categories or global settings
      fetch(`${BACKEND_URL}/api/categories`)
        .then(res => res.json())
        .catch(err => console.log('Failed to preload categories'));
    }
  }, [isBackendAwake]);

  return (
    <div className="App">
      <ScrollToTop />
      <ScrollingText />
      <Navbar />
      
      {!isBackendAwake && (
        <div className="backend-loading">
          <p>Just a moment, we're preparing things for you...</p>
          <LoadingSpinner />
        </div>
      )}

      <Suspense fallback={<div className="page-loading"><LoadingSpinner /></div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/collection" element={<Collection />} />
          <Route path="/about" element={<About />} />
          <Route path="/product/:productId" element={<ProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/list" element={<ListProduct />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/return-policy" element={<RefundReturnPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/customize" element={<CustomizePage />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/ContactUS" element={<ContactUs />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/blog/:id" element={<BlogDetail />} />
          <Route path="/curated/:collectionId" element={<SubCollections />} />
          <Route path="/admin/login" element={<Login />} />
          
          <Route element={<ProtectedRoute />}>
            <Route path="/admin/*" element={<AdminLayout />}>
              <Route path="addproduct" element={<AdminPage />} />
              <Route path="list-products" element={<ListProduct />} />
              <Route path="testimonials" element={<TestimonialForm />} />
              <Route path="banners" element={<BannerPanel />} />
              <Route path="blog-form" element={<BlogForm />} />
              <Route path="blog-list" element={<BlogList />} />
              <Route path="categorypanel" element={<CategoryPanel />} />
              <Route path="curatedcollectionform" element={<CuratedCollectionForm />} />
              <Route path="curatedcollectionlist" element={<CuratedCollectionsList />} />
              <Route path="createcoupon" element={<CreateCouponForm />} />
              <Route path="couponlist" element={<CouponList />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      
      <Footer />
    </div>
  );
}

export default App;